import { useHistory } from 'react-router-dom';
import { PAGE_PARAM_NAME, START_PAGE } from 'shared/ui/Pagination/constants';

export const useUrlResolver = () => {
  const history = useHistory();

  const getUrl = () => {
    return new URL(window.location.href);
  };

  const hasParam = (name: string, value?: string) => {
    const url = getUrl();

    return url.searchParams.has(name, value);
  };

  const openNewTab = (name: string, value: string) => {
    const url = getUrl();
    const hasParam = url.searchParams.get(name);

    if (hasParam) {
      url.searchParams.delete(name);
    }

    url.searchParams.append(name, value);

    window.open(url);
  };
  const addParam = (name: string, value: string, onlyOne = true) => {
    const url = getUrl();
    const hasParam = url.searchParams.get(name);

    if (hasParam && onlyOne) {
      url.searchParams.delete(name);
    }
    url.searchParams.append(name, value);
    history.push({ search: url.searchParams.toString() });
  };
  const getParam = (name: string) => {
    const url = getUrl();

    return url.searchParams.get(name);
  };

  const getAllParams = (name: string) => {
    const url = getUrl();

    return url.searchParams.getAll(name);
  };
  const removeParam = (name: string) => {
    const url = getUrl();

    url.searchParams.delete(name);
    history.push({ search: url.searchParams.toString() });
  };

  const removeParamFromUrl = ({ name, url, value }: { name: string; url: URL; value?: string }) => {
    url.searchParams.delete(name, value);

    return url;
  };

  const addParamToUrl = ({
    name,
    value,
    url,
    onlyOne = true,
  }: {
    name: string;
    value: string;
    url: URL;
    onlyOne?: boolean;
  }) => {
    const hasParam = url.searchParams.get(name);

    if (hasParam && onlyOne) {
      url.searchParams.delete(name);
    }
    url.searchParams.append(name, value);

    return url;
  };

  const updateHistory = ({ url, pathname }: { url: URL; pathname?: string }) => {
    history.push({ pathname, search: url.searchParams.toString() });
  };

  const hasFilter = () => {
    const url = getUrl();

    return url.search.length > 0;
  };

  return {
    getUrl,
    openNewTab,
    hasParam,
    hasFilter,
    addParam,
    getParam,
    removeParam,
    updateHistory,
    addParamToUrl,
    removeParamFromUrl,
    getAllParams,
  };
};

export const isCorrectQueryParams = (
  url: string,
  { inQuery, outOfQuery }: { inQuery?: Record<string, string>; outOfQuery?: Record<string, string> }
) => {
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);

  const queryParams = Object.fromEntries(params.entries());
  let isCorrectQuery = true;

  if (inQuery) {
    for (const [key, value] of Object.entries(inQuery)) {
      if (queryParams[key] !== value) {
        isCorrectQuery = false;
      }
    }
  }

  if (outOfQuery) {
    for (const [key, value] of Object.entries(outOfQuery)) {
      if (queryParams[key] === value) {
        isCorrectQuery = false;
      }
    }
  }

  return isCorrectQuery;
};

export const usePaginationReset = () => {
  const { getUrl, updateHistory, hasParam, removeParamFromUrl, addParamToUrl } = useUrlResolver();

  return () => {
    const url = getUrl();

    const hasPage = hasParam(PAGE_PARAM_NAME);

    if (hasPage) {
      removeParamFromUrl({ name: PAGE_PARAM_NAME, url });
      addParamToUrl({ name: PAGE_PARAM_NAME, value: START_PAGE, url });
    }

    updateHistory({ url });
  };
};
